/**
 * Text page-object helpers: add/update text objects, build
 * the sanitized SVG string used for PDF generation, and parse template-position
 * text attributes.
 */

import type { PageObjectModelChangeResult } from '@sosocio/creator-types/app';
import type {
	OfferingModel,
	TemplatePositionTextAttributesModel,
	TemplatePositionTextModel,
} from '@sosocio/creator-types/database';
import type {
	PageModel,
	PageObjectModel,
} from '@sosocio/creator-types/project';
import {
	createTextSVGElement as createSharedTextSVGElement,
	createTextSVGString as createSharedTextSVGString,
} from '@sosocio/tools/page-object-text-svg';
import { nanoid } from 'nanoid';
import {
	FontModule,
	ProductStateModule,
} from 'store';
import defaults from 'store/modules/productstate/defaults';
import {
	getFontFamily,
	getTextObjectFontSize,
	isTextObjectFontSizeSource,
	MIN_FONTSIZE,
	pointToPixel,
} from 'utils/font';
import reportError from 'utils/report-error';
import {
	filter,
	fit,
} from 'utils/text';
import * as mobileTools from '../is-mobile';
import {
	hasTemplatePositionTextAttributes,
	normalizeTemplatePositionTextAttributes,
} from '../template-position-text-attributes';
import {
	getBoundingBox,
	getRotatedCorners,
} from './geometry-primitives';

export interface AddTextObjectOptions {
	objectModel: Partial<PageObjectModel>;
	offeringModel?: OptionalExceptFor<OfferingModel, 'minfontsize'>;
	pageModel: PageModel;
	pageObjects: PageObjectModel[];
	selectedFreeTextObject?: PageObjectModel | null;
	text: string;
}

interface AddTextObjectPosition {
	x: number;
	y: number;
}

export interface UpdateTextObjectOptions {
	newData: Partial<PageObjectModel>;
	objectModel: PageObjectModel;
	offeringModel?: OptionalExceptFor<OfferingModel, 'minfontsize'>;
	pageModel: PageModel;
}

const ADD_TEXT_CASCADE_GAP_RATIO = 0.1;

export async function addTextObject(options: AddTextObjectOptions): Promise<PageObjectModel | undefined> {
	const {
		objectModel,
		offeringModel,
		pageModel,
		pageObjects,
		selectedFreeTextObject: capturedSelectedFreeTextObject,
		text,
	} = options;
	const height = Math.round(Math.min(
		pageModel.width / 4,
		pageModel.height / 4,
	));
	const width = (
		!mobileTools.getIsMobile()
			? Math.round(pageModel.width / 2)
			: pageModel.width
	);
	const defaultPosition: AddTextObjectPosition = {
		x: (
			!mobileTools.getIsMobile()
				? Math.round(pageModel.width / 2 - (width / 2))
				: 0
		),
		y: Math.round(pageModel.height / 2 - (height / 2)),
	};
	const selectedFreeTextObjectCandidate = (
		capturedSelectedFreeTextObject === undefined
			? pageObjects.find((pageObject) => (
				pageObject.type === 'text'
				&& pageObject._selected
			))
			: capturedSelectedFreeTextObject
	);
	const selectedFreeTextObject = (
		selectedFreeTextObjectCandidate?.type === 'text'
		&& (
			selectedFreeTextObjectCandidate.templatestateid === null
			|| typeof selectedFreeTextObjectCandidate.templatestateid === 'undefined'
		)
			? selectedFreeTextObjectCandidate
			: undefined
	);
	const selectedFreeTextTypography: Partial<PageObjectModel> = (
		selectedFreeTextObject
			? {
				align: selectedFreeTextObject.align,
				fontbold: selectedFreeTextObject.fontbold,
				fontcolor: selectedFreeTextObject.fontcolor,
				fontcolor_pantone: selectedFreeTextObject.fontcolor_pantone,
				fontcolor_visual: selectedFreeTextObject.fontcolor_visual,
				fontface: selectedFreeTextObject.fontface,
				fontitalic: selectedFreeTextObject.fontitalic,
				fontunderline: selectedFreeTextObject.fontunderline,
				...(
					isTextObjectFontSizeSource(selectedFreeTextObject)
						? {
							fontsize: getTextObjectFontSize(selectedFreeTextObject),
						}
						: {}
				),
			}
			: {}
	);
	const maxZAxis = (
		pageObjects?.reduce(
			(
				maxZ,
				pageObject,
			) => {
				if (pageObject.editable) {
					return Math.max(
						maxZ,
						pageObject.z_axis,
					);
				}

				return maxZ;
			},
			0,
		)
		?? 0
	);

	const updatedTextObject = (await updateTextObject({
		objectModel: {
			...JSON.parse(JSON.stringify(defaults.objectModel)),
			...objectModel,
			fontface: FontModule.getDefault.id,
			fontsize: Math.max(
				MIN_FONTSIZE,
				Math.round(pageModel.height / 20),
			),
			...selectedFreeTextTypography,
			id: nanoid(),
			height,
			text,
			type: 'text',
			version: '12.30.0',
			width,
			x_axis: defaultPosition.x,
			y_axis: defaultPosition.y,
			z_axis: maxZAxis + 1,
		},
		newData: {
			text,
		},
		offeringModel,
		pageModel,
	}))?.updatedPageObject;

	if (!updatedTextObject) {
		return undefined;
	}

	let finalTextObject: PageObjectModel = {
		...updatedTextObject,
		...selectedFreeTextTypography,
		x_axis: (
			!mobileTools.getIsMobile()
				? Math.round(pageModel.width / 2 - (updatedTextObject.width / 2))
				: 0
		),
		y_axis: Math.round(pageModel.height / 2 - (updatedTextObject.height / 2)),
	};

	if (selectedFreeTextObject) {
		const selectedBoundingBox = getBoundingBox(getRotatedCorners(selectedFreeTextObject));
		const selectedVisualHeight = selectedBoundingBox.maxY - selectedBoundingBox.minY;
		const horizontallyAlignedTextObject = {
			...finalTextObject,
			x_axis: Math.round(selectedFreeTextObject.x_axis),
		};
		const alignedBoundingBox = getBoundingBox(getRotatedCorners(horizontallyAlignedTextObject));
		const desiredBoundingBoxTop = (
			selectedBoundingBox.maxY
			+ selectedVisualHeight * ADD_TEXT_CASCADE_GAP_RATIO
		);
		const cascadedTextObject = {
			...horizontallyAlignedTextObject,
			y_axis: Math.round(
				horizontallyAlignedTextObject.y_axis
				+ desiredBoundingBoxTop
				- alignedBoundingBox.minY,
			),
		};
		const cascadedBoundingBox = getBoundingBox(getRotatedCorners(cascadedTextObject));
		const isCascadedPositionInsidePage = (
			cascadedBoundingBox.minX >= 0
			&& cascadedBoundingBox.minY >= 0
			&& cascadedBoundingBox.maxX <= pageModel.width
			&& cascadedBoundingBox.maxY <= pageModel.height
		);

		if (isCascadedPositionInsidePage) {
			finalTextObject = cascadedTextObject;
		}
	}

	return finalTextObject;
}

/**
 * Constructs an SVG element that represents a text {@link PageObjectModel}
 * using its typography, alignment, background, and positional attributes. The SVG
 * disables contextual OpenType features to keep glyph shaping consistent.
 *
 * @param objectModel {@link PageObjectModel} containing the dimensions, font
 * properties, optional background color, positional attributes, and text content
 * used to build the SVG.
 * @returns Detached SVG element reflecting the provided {@link PageObjectModel}.
 */
export function createTextSVGElement(objectModel: PageObjectModel): SVGSVGElement {
	return createSharedTextSVGElement({
		fontModel: FontModule.getById(objectModel.fontface!),
		objectModel,
	});
}

/**
 * Constructs sanitized SVG markup for a text {@link PageObjectModel}.
 *
 * @param objectModel {@link PageObjectModel} containing the text and rendering
 * properties used to generate the SVG markup.
 * @returns Stringified SVG markup with HTML entities removed for downstream PDF generation.
 */
export function createTextSVGString(objectModel: PageObjectModel): string {
	return createSharedTextSVGString({
		fontModel: FontModule.getById(objectModel.fontface!),
		objectModel,
	});
}

/**
 * Resolves the attributes for a {@link TemplatePositionTextModel}, parsing JSON
 * strings when present and normalizing legacy flat style maps to the structured
 * attributes shape. When parsing fails, the error is reported to Bugsnag as a
 * warning so malformed data can be observed without interrupting user flows.
 *
 * @param positionModel {@link TemplatePositionTextModel} containing attributes
 * either as a JSON string or typed object.
 * @returns The normalized {@link TemplateTextPosition} attributes, or `null`
 * when no attributes are available or parsing fails.
 */
export function getAndParseTemplatePositionTextModelAttributes(positionModel: TemplatePositionTextModel): TemplatePositionTextAttributesModel | null {
	let attributes: TemplatePositionTextAttributesModel = {};

	if (typeof positionModel.attributes === 'string') {
		try {
			attributes = normalizeTemplatePositionTextAttributes(JSON.parse(positionModel.attributes));
		} catch {
			reportError(
				new Error(`Could not parse TemplatePositionTextModel attributes JSON string for TemplatePositionStateModel ID ${positionModel.id}`),
				'warning',
			);
		}
	} else if (
		typeof positionModel.attributes !== 'undefined'
		&& positionModel.attributes !== null
	) {
		attributes = normalizeTemplatePositionTextAttributes(positionModel.attributes);
	}

	if (hasTemplatePositionTextAttributes(attributes)) {
		return attributes;
	}

	return null;
}

/**
 * Determines whether deselection should remove a text object that has no user-authored content.
 *
 * @param objectModel Page object leaving its selected editing state.
 * @returns True for empty text or newly inserted starter text that the user has not changed.
 */
export function shouldDeleteTextObjectOnDeselect(objectModel: PageObjectModel): boolean {
	return (
		objectModel.type === 'text'
		&& (
			!objectModel.text
			|| !!objectModel._isNewTextObjectUntouched
		)
	);
}

export function updateTextObject(options: UpdateTextObjectOptions): Promise<PageObjectModelChangeResult | undefined> {
	const {
		objectModel,
		offeringModel,
		pageModel,
	} = options;
	let { newData } = options;
	/**
	 * Clone data to avoid mutating the original object
	 */
	newData = {
		...newData,
	};
	const fontModel = (
		objectModel.fontface
		&& FontModule.getById(objectModel.fontface)
	);

	if (!fontModel) {
		throw new Error('Could not find required font model');
	}

	if (typeof newData.text !== 'undefined') {
		newData.text = filter(
			newData.text,
			fontModel.subset.split(','),
		);
	}

	const fontSizeSource = (
		isTextObjectFontSizeSource(newData)
			? newData
			: objectModel
	);

	if (!isTextObjectFontSizeSource(fontSizeSource)) {
		throw new Error('Missing required text object font size');
	}

	let fontSize = getTextObjectFontSize(fontSizeSource);

	const mergedObjectModel: PageObjectModel = {
		...objectModel,
		...newData,
		fontsize: fontSize,
	};

	if (
		newData.height
		|| newData.width
	) {
		const fitResult = fit({
			fontFamily: getFontFamily({
				family: mergedObjectModel.fontface!,
				bold: !!mergedObjectModel.fontbold,
				italic: !!mergedObjectModel.fontitalic,
			}),
			fontModel: FontModule.getById(mergedObjectModel.fontface!)!,
			fontSize,
			isBold: !!mergedObjectModel.fontbold,
			maxWidth: mergedObjectModel.width,
			text: (
				mergedObjectModel.text
				?? ''
			),

			kind: 'font',
			boxHeight: mergedObjectModel.height,
			boxWidth: mergedObjectModel.width,
			minFontSize: (
				offeringModel?.minfontsize
					? pointToPixel(offeringModel.minfontsize)
					: MIN_FONTSIZE
			),
			maxFontSize: pageModel.height,
		});

		if (mergedObjectModel.fontsize !== fitResult.usedFontSize) {
			newData.fontsize = fitResult.usedFontSize;
			fontSize = fitResult.usedFontSize;
		}
	} else if (
		(
			typeof newData.text !== 'undefined'
			|| typeof newData.fontbold !== 'undefined'
			|| typeof newData.fontface !== 'undefined'
			|| typeof newData.fontitalic !== 'undefined'
			|| typeof newData.fontunderline !== 'undefined'
			|| typeof newData.fontsize !== 'undefined'
			|| typeof newData.pointsize !== 'undefined'
		)
		&& objectModel.transformable
	) {
		const fitResult = fit(
			{
				fontFamily: getFontFamily({
					family: mergedObjectModel.fontface!,
					bold: !!mergedObjectModel.fontbold,
					italic: !!mergedObjectModel.fontitalic,
				}),
				fontModel: FontModule.getById(mergedObjectModel.fontface!),
				fontSize,
				isBold: !!mergedObjectModel.fontbold,
				maxWidth: mergedObjectModel.width,
				text: (
					mergedObjectModel.text
					?? ''
				),

				kind: 'box',
				allowGrow: true,
				allowShrink: true,
			},
		);

		if (fitResult.height !== mergedObjectModel.height) {
			newData.height = Math.round(fitResult.height);
		}
		if (fitResult.usedFontSize !== fontSize) {
			newData.fontsize = fitResult.usedFontSize;
		}
	}

	return ProductStateModule.changePageObject({
		id: objectModel.id,
		...newData,
		noStore: true,
		originalPageObject: objectModel,
	});
}
